import React, { useEffect, useState } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { styled } from "@mui/styles";
import {
    Card as MuiCard,
    CardContent as MuiCardContent,
    Typography,
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    CircularProgress,
    IconButton,
    Avatar,
    Tooltip,
} from "@mui/material";
import {
    Edit as EditIcon,
    Replay as UndoIcon,
    Save as SaveIcon,
    AssignmentOutlined as CopyIcon,
} from "@mui/icons-material";
import { spacing } from "@mui/system";
import useCaseSummary, { invalidate } from "../hooks/queries/useCaseSummary";
import { format } from "date-fns";
import PerfectScrollbar from "react-perfect-scrollbar";
import "../vendor/perfect-scrollbar.css";
import Markdown from "markdown-to-jsx"
import actionCreators from "../redux/actions/caseSummaryActionCreators";
import avatarStyles from "../theme/avatars";
import MyCKEditor from '../ckeditor/MyCKEditor';
import axios from "../plugins/axios";

const Card = styled(MuiCard)(spacing);

const CardContent = styled(MuiCardContent)(({ theme }) => ({
    '&:last-child': {
        paddingBottom: theme.spacing(4)
    }
}));


const CaseSummaryCard = ({ caseState, requestSummary, clearAlert, users }) => {

    const avatarClasses = avatarStyles();

    const { currentCaseId: caseId, cases } = caseState;

    const { data: caseSummary, isLoading } = useCaseSummary(caseId);

    const c = cases[caseId];  

    const { requested } = c.summaryState;

    const [dialogOpen, setDialogOpen] = useState(false);

    const [editing, setEditing] = useState(false);

    const [saving, setSaving] = useState(false);

    const [editor, setEditor] = useState();

    useEffect(() => {
        clearAlert(caseId)
    }, [caseId, caseSummary?.id, clearAlert]);

    const isProductionEnvironment = process.env.REACT_APP_ENV === "production" || process.env.REACT_APP_ENV === "preprod";

    const handleButtonClick = () => {
        if (caseSummary)
            setDialogOpen(true);

        else if (!requested)
            requestSummary(caseId, 0);
    }

    const edit = () => {
        setEditing(true);
    }

    const save = async () => {

        let summary = editor.getData();

        setEditing(false);
        setSaving(true);

        try {
            await axios.post(`${process.env.REACT_APP_CASENEST_API_URL}/casesummary`, { caseId, summary });
        }
        finally {
            invalidate(caseId);
            setSaving(false);
        }        
    }

    const copy = () => {        

        try {
            navigator.clipboard.write([new ClipboardItem({
                'text/plain': new Blob([caseSummary.summary], { type: 'text/plain' })
            })])
        }
        catch (err) {
            console.error(err);
        }
    }

    const user = users[caseSummary?.userId];

    const info = () => caseSummary &&
        <Box display="flex" alignItems="center">
            <Tooltip title={`${caseSummary.isUserEdit ? "Edited" : "Requested"} by ${user?.name}`} >
                <Avatar src={user?.photo} alt={user?.name || "--"} className={avatarClasses.small} />
            </Tooltip>
            &nbsp;
            {format(new Date(caseSummary.createdOn), "dd/MM/yy HH:mm")}            
        </Box>

    if (!isProductionEnvironment || (isLoading && !caseSummary))
        return null;

    return (
        <>
            <Card mb={6}>
                <CardContent pb={0}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Case Summary</Typography>
                        {info()}
                        <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={handleButtonClick}
                            disabled={requested}>
                            {caseSummary ? "View" : requested ? "Requested .." : "Request"}
                        </Button>
                    </Box>
                </CardContent>
            </Card>
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                fullWidth
                maxWidth="xl"
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" mr={3}>
                    <Box display="flex">
                        <DialogTitle>Case Summary</DialogTitle>                        
                        {info()}
                    </Box>                    
                    {!editing &&
                        <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => requestSummary(caseId, caseSummary.id)}
                            disabled={requested}>
                            {(requested) ? "Requested .." : "Request New"}
                        </Button>
                    }
                    <Box>
                        {!editing && !requested && !saving && (
                            <IconButton onClick={edit}>
                                <EditIcon />
                            </IconButton>
                        )}
                        {editing && (
                            <IconButton
                                onClick={save}
                                size="large">
                                <SaveIcon />
                            </IconButton>
                        )}
                        {editing && (
                            <IconButton onClick={() => setEditing(false)}>
                                <UndoIcon />
                            </IconButton>
                        )}
                        {saving && <CircularProgress size={21} />}
                        {!editing && !saving && (
                            <IconButton onClick={copy}>
                                <CopyIcon />
                            </IconButton>
                        )}
                    </Box>
                </Box>
                <DialogContent>
                    {caseSummary &&
                        <PerfectScrollbar style={{ maxHeight: '600px' }}>
                            {editing
                                ? <MyCKEditor
                                    initialData={caseSummary.summary}
                                    setEditor={setEditor}
                                    onChange={() => { }}
                                    onFocus={() => { }}
                                    type="markdown"
                                />
                                : <Markdown>{caseSummary.summary}</Markdown>}
                        </PerfectScrollbar>}
                </DialogContent>
            </Dialog>
        </>
    );
};

const mapStateToProps = (state) => ({
    caseState: state.caseReducer,
    users: state.userReducer.users,
});

const mapDispatchToProps = (dispatch) => {
    return {
        ...bindActionCreators(actionCreators, dispatch),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(CaseSummaryCard);

